import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { FirstLeadGuidance } from 'app/features/leads/components/first-lead-guidance';
import { LeadsGuideModal } from 'app/features/leads/components/guide/leads-guide-modal';
import { LeadsEmptyState } from 'app/features/leads/components/leads-empty-state';
import { LeadsFilteredEmpty } from 'app/features/leads/components/leads-filtered-empty';
import { LeadsPaginationFooter } from 'app/features/leads/components/leads-pagination-footer';
import { LeadsTable } from 'app/features/leads/components/leads-table';
import { LeadsToolbar } from 'app/features/leads/components/leads-toolbar';
import Button from 'app/ui/components/atoms/button';
import { LeadsQueryProvider } from 'app/features/leads/providers/leads-query-provider';
import { leadsListQueryOptions } from 'app/features/leads/queries/leads-list-query-options';
import type { LeadStatusFilter, LeadSummary, LeadsListParams } from 'app/features/leads/services/gym-finder/types';
import { getLeadsPageState } from 'app/features/leads/utils/get-leads-page-state';
import { routes } from 'app/routes/constants';
import ErrorComponent from 'app/ui/components/molecules/error';
import GenericPage from 'app/ui/components/layouts/generic-page';
import Loading from 'app/ui/components/molecules/loading';
import PageTitle from 'app/ui/components/molecules/page-title';
import Typography from 'app/ui/components/atoms/typography';
import { trackEvent } from 'app/utils/analytics/amplitude/tracker';
import useServices from 'app/services';

const SEARCH_DEBOUNCE_MS = 300;
export const LEADS_PAGE_SIZE = 50;

const isEmailSearchTerm = (search: string) => search.includes('@') || (search.includes('.') && !/\s/.test(search));

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme: { spacing } }) => spacing(4)};
  margin: ${({ theme: { spacing } }) => `${spacing(8)} 0 ${spacing(6)}`};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${({ theme: { spacing } }) => spacing(4)};

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        width: auto;
      `,
    )}
  `}
`;

const GuideTriggerWrapper = styled.span`
  display: block;
  width: 100%;

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        width: auto;
      `,
    )}
  `}
`;

const LeadsPageContent = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { hyrox365 } = useServices();
  const pageTitle = intl.formatMessage({ id: 'leads.pageTitle' });

  const [status, setStatus] = useState<LeadStatusFilter>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isFirstLeadGuidanceDismissed, setIsFirstLeadGuidanceDismissed] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([]);
  const debouncedSearchRef = useRef('');

  const resetPagination = useCallback(() => {
    setCursor(undefined);
    setCursorHistory([]);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const nextSearch = search.trim();
      if (nextSearch !== debouncedSearchRef.current) {
        debouncedSearchRef.current = nextSearch;
        setDebouncedSearch(nextSearch);
        resetPagination();
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [resetPagination, search]);

  const listParams = useMemo((): LeadsListParams => {
    const params: LeadsListParams = { limit: LEADS_PAGE_SIZE };
    if (cursor) {
      params.cursor = cursor;
    }
    if (debouncedSearch) {
      if (isEmailSearchTerm(debouncedSearch)) {
        params.email = debouncedSearch;
      } else {
        params.name = debouncedSearch;
      }
    }
    if (status !== 'all') {
      params.status = status;
    }
    return params;
  }, [cursor, debouncedSearch, status]);

  const { data, isPending, isError, isFetching } = useQuery(leadsListQueryOptions(hyrox365, listParams));
  const leads = data?.leads ?? [];

  const currentPage = cursorHistory.length + 1;
  const hasNext = Boolean(data?.nextCursor);
  const hasPrevious = cursorHistory.length > 0;
  const showPagination = hasNext || hasPrevious;

  const handleNext = useCallback(() => {
    if (!data?.nextCursor) {
      return;
    }

    setCursorHistory((history) => [...history, cursor]);
    setCursor(data.nextCursor);
  }, [cursor, data?.nextCursor]);

  const handlePrevious = useCallback(() => {
    const previousCursor = cursorHistory[cursorHistory.length - 1];
    setCursor(previousCursor);
    setCursorHistory((history) => history.slice(0, -1));
  }, [cursorHistory]);

  const handleStatusChange = useCallback(
    (nextStatus: LeadStatusFilter) => {
      if (nextStatus === status) {
        return;
      }

      setStatus(nextStatus);
      resetPagination();
    },
    [resetPagination, status],
  );

  useEffect(() => {
    trackEvent('leads_page_viewed', {});
  }, []);

  const handleOpenGuide = useCallback(() => {
    trackEvent('leads_guide_opened', {});
    setIsGuideOpen(true);
  }, []);

  const handleCloseGuide = useCallback(() => {
    setIsGuideOpen(false);
  }, []);

  const handleViewDetails = useCallback(
    (lead: LeadSummary) => {
      trackEvent('leads_view_details_clicked', { leadId: lead.id });
      navigate(routes.LEAD_DETAIL.replace(':leadId', lead.id));
    },
    [navigate],
  );

  const handleDismissFirstLeadGuidance = useCallback(() => {
    setIsFirstLeadGuidanceDismissed(true);
  }, []);

  if (isPending && !data) {
    return <Loading />;
  }

  if (isError || !data) {
    return <ErrorComponent />;
  }

  const pageState = getLeadsPageState({
    totalCount: data.totalCount,
    filteredCount: leads.length,
    profileComplete: data.profileComplete,
    search: debouncedSearch,
    status,
    isFirstPage: !cursor,
  });

  const isProfileIncomplete = pageState === 'zero-leads-profile-incomplete';

  const guideTriggerLabel = intl.formatMessage({ id: 'leads.guide.trigger' });

  const paginationFooter = showPagination ? (
    <LeadsPaginationFooter
      currentPage={currentPage}
      hasPrevious={hasPrevious}
      hasNext={hasNext}
      onPrevious={handlePrevious}
      onNext={handleNext}
      isLoading={isFetching}
    />
  ) : null;
  const isUpdatingLeads = isFetching && !isPending;

  return (
    <GenericPage pageTitle={pageTitle}>
      <HeaderRow>
        <PageTitle>
          <Typography variant="h3">{pageTitle}</Typography>
        </PageTitle>
        <HeaderActions>
          <GuideTriggerWrapper data-testid="leads-guide-trigger">
            <Button type="secondary-white" label={guideTriggerLabel} onClick={handleOpenGuide} fullWidthMob={true} />
          </GuideTriggerWrapper>
        </HeaderActions>
      </HeaderRow>

      {isProfileIncomplete ? <LeadsEmptyState /> : null}

      {pageState === 'first-lead' ? (
        <>
          {!isFirstLeadGuidanceDismissed ? <FirstLeadGuidance onDismiss={handleDismissFirstLeadGuidance} /> : null}
          <LeadsToolbar
            search={search}
            status={status}
            onSearchChange={setSearch}
            onStatusChange={handleStatusChange}
          />
          <LeadsTable leads={leads} isLoading={isUpdatingLeads} onViewDetails={handleViewDetails} />
          {paginationFooter}
        </>
      ) : null}

      {pageState === 'filtered-empty' ? (
        <>
          <LeadsToolbar
            search={search}
            status={status}
            onSearchChange={setSearch}
            onStatusChange={handleStatusChange}
          />
          <LeadsFilteredEmpty />
        </>
      ) : null}

      {!isProfileIncomplete && pageState === 'table' ? (
        <>
          <LeadsToolbar
            search={search}
            status={status}
            onSearchChange={setSearch}
            onStatusChange={handleStatusChange}
          />
          <LeadsTable leads={leads} isLoading={isUpdatingLeads} onViewDetails={handleViewDetails} />
          {paginationFooter}
        </>
      ) : null}

      <LeadsGuideModal isOpen={isGuideOpen} onClose={handleCloseGuide} />
    </GenericPage>
  );
};

const LeadsPage = () => (
  <LeadsQueryProvider>
    <LeadsPageContent />
  </LeadsQueryProvider>
);

export default LeadsPage;
